/**
 * Visitor Context Utility
 *
 * Captures client-side context: page URL, timezone, and device type.
 * IP address, country, and other headers are captured server-side by the BFF.
 *
 * Context is sent on EVERY message. The backend uses "merge" semantics:
 * - Non-null values update existing fields
 * - Null/undefined values are ignored (existing values preserved)
 */

/**
 * Strips query parameters and hash from a URL for privacy.
 * Query params often contain sensitive tokens, UTM parameters, or PII.
 * Returns only origin + pathname (e.g., "https://example.com/admissions")
 *
 * Returns undefined if URL parsing fails to avoid leaking potentially
 * sensitive data or causing server-side validation failures.
 */
function stripSensitiveUrlParts(url: string): string | undefined {
  try {
    const parsed = new URL(url);
    return parsed.origin + parsed.pathname;
  } catch {
    // If URL parsing fails, omit the URL to avoid leaking sensitive data
    return undefined;
  }
}

/**
 * Gets the current page URL (origin + pathname only, no query params or hash)
 * Returns the parent window's URL when embedded in an iframe
 *
 * PRIVACY: Query parameters and hash fragments are stripped to avoid
 * leaking sensitive data (tokens, UTM params, PII in URLs).
 */
export function getPageUrl(): string | undefined {
  try {
    if (typeof window === 'undefined') return undefined;

    let rawUrl: string | undefined;

    // Try to get the parent window's URL (when widget is in an iframe)
    // This will throw if cross-origin, in which case we fall back to referrer
    try {
      if (window.parent !== window && window.parent.location.href) {
        rawUrl = window.parent.location.href;
      }
    } catch {
      // Cross-origin iframe - use referrer as fallback
      if (document.referrer) {
        rawUrl = document.referrer;
      }
    }

    // Fallback to current window location
    if (!rawUrl) {
      rawUrl = window.location.href;
    }

    // Strip query params and hash for privacy
    return stripSensitiveUrlParts(rawUrl);
  } catch (error) {
    console.warn('[VisitorContext] Failed to get page URL:', error);
    return undefined;
  }
}

/**
 * Get user's timezone using Intl API.
 * Returns IANA timezone identifier (e.g., "America/Toronto", "Europe/London").
 */
export function getTimezone(): string | undefined {
  try {
    if (typeof window === 'undefined') return undefined;
    return Intl.DateTimeFormat().resolvedOptions().timeZone;
  } catch {
    return undefined;
  }
}

/**
 * Determine device type based on screen width.
 * - mobile: < 768px
 * - tablet: 768px - 1024px
 * - desktop: > 1024px
 */
export function getDeviceType(): 'desktop' | 'tablet' | 'mobile' {
  if (typeof window === 'undefined') return 'desktop';
  const width = window.innerWidth || document.documentElement.clientWidth;
  if (width < 768) return 'mobile';
  if (width <= 1024) return 'tablet';
  return 'desktop';
}

/**
 * Client context captured by the widget.
 * Sent on EVERY message - backend uses merge semantics.
 * Server-side context (IP, country, headers) is captured by the BFF.
 */
export interface ClientContext {
  pageUrl?: string;
  timezone?: string;
  deviceType?: 'desktop' | 'tablet' | 'mobile';
}

/**
 * Get client context for the current message.
 * This includes page URL, timezone, and device type.
 *
 * Called on every message - backend will only update
 * fields that have non-null values (merge semantics).
 */
export function getClientContext(): ClientContext {
  return {
    pageUrl: getPageUrl(),
    timezone: getTimezone(),
    deviceType: getDeviceType(),
  };
}
